import { autoinject, computedFrom, transient } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { ManageVesselService } from "../../../../../services/vessels/manage-vessel.service";
import { ValidationController, ValidationRules, validationMessages } from "aurelia-validation";
import { NotificationClientService, NotificationType } from "../../../../../services/basics/notification-client.service";
import { CustomValidationRenderer } from "../../../../../client-validation-system/custom-validation-renderer";
import { ValidationsClientService } from "../../../../../services/validations/validations-client.service";
import { LookupValue } from "../../../../../models/lookups/lookup.models";
import { AddVesselModel } from "../../../../../models/vessels/vessel.models";
import { EntityTypeConfigurationHelper } from "../../../../../models/basics/basics.models";
import { BindingEngine, Disposable } from "aurelia-binding";

@autoinject
@transient()
export class AddCapSurveyDialog {

    public vessel: AddVesselModel;
    public companyEntityTypeConfigurationModel;
    public avoidNotificationTag = 'avoid-notification';
    public subscriptions: Disposable[] = [];

    public set vesselType(value: LookupValue) {
        if (value && value.additionalData && value.additionalData.code) {
            this.vessel.vesselTypeCode = value.additionalData.code;
        }
    }

    public set vesselStatus(value: LookupValue) {
        if (value && value.additionalData && value.additionalData.code) {
            this.vessel.vesselStatusCode = value.additionalData.code;
        }
    }

    public set hullType(value: LookupValue) {
        if (value && value.additionalData && value.additionalData.code) {
            this.vessel.hullTypeCode = value.additionalData.code;
        }
    }

    public set flag(value: LookupValue) {
        if (value && value.additionalData && value.additionalData.code) {
            this.vessel.flagCode = value.additionalData.code;
        }
    }

    public set docCompany(value) {
        if (value && value && value.owCode) {
            this.vessel.docCompanyCode = value.owCode;
        }
    }

    constructor(private vesselService: ManageVesselService,
        private dialogClientService: DialogClientService,
        private validationController: ValidationController,
        private notificationClientService: NotificationClientService,
        private validationsClientService: ValidationsClientService,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper,
        private bindingEngine: BindingEngine) {
        this.validationController.addRenderer(new CustomValidationRenderer());
        validationMessages['required'] = `\${$displayName} is required.`;
        this.companyEntityTypeConfigurationModel = this.entityTypeConfigurationHelper.getConfigurationByKey(["BasicCompanySearch"]);
    }

    attached() {
        this.subscriptions.push(
            this.bindingEngine.propertyObserver(this.vessel, 'imo')
                .subscribe(this.imoVinEniChanged.bind(this)),
            this.bindingEngine.propertyObserver(this.vessel, 'eni')
                .subscribe(this.imoVinEniChanged.bind(this)),
            this.bindingEngine.propertyObserver(this.vessel, 'vin')
                .subscribe(this.imoVinEniChanged.bind(this)),
        );
    }

    detached() {
        this.subscriptions.forEach((subscription: Disposable) => subscription.dispose());
    }

    protected activate() {
        this.initializeNew();
    }

    public vesselTypeDisplay(item: LookupValue) {
        if (item) {
            return `${item.name} ${(item.additionalData && item.additionalData.code ? `(${item.additionalData.code})` : "")}`;
        }

        return "";
    }

    private initializeNew() {
        const item: AddVesselModel = {
            vesselName: null,
            imo: null,
            eni: null,
            vin: null,
            vesselStatusId: null,
            vesselTypeId: null,
            flagId: null,
            docCompanyId: null,
            dateOfBuild: null,
            dwt: null,
            nrt: null,
            loa: null,
            breadthMoulded: null,
            breadthExtreme: null,
            draught: null,
            hullTypeId: null,
        };

        this.vessel = item;

        this.addValidationRules();
    }

    private addValidationRules() {
        //ValidationRules with the tag 'avoid-notification' will not be displayed in the 'Validation Error' notification box
        ValidationRules
            .ensure<AddVesselModel, any>(x => x.vesselName)
            .displayName('Vessel Name')
            .required()
            .ensure(x => x.imo)
            .displayName('IMO')
            .required()
            .tag(this.avoidNotificationTag)
            .when(x => (!x.imo || x.imo.trim() == "") && (!x.eni || x.eni.trim() == "") && (!x.vin || x.vin.trim() == ""))
            .withMessage("Either IMO/ENI/VIN must be entered.")
            .ensure(x => x.eni)
            .displayName('ENI')
            .required()
            .tag(this.avoidNotificationTag)
            .when(x => (!x.imo || x.imo.trim() == "") && (!x.eni || x.eni.trim() == "") && (!x.vin || x.vin.trim() == ""))
            .withMessage("Either IMO/ENI/VIN must be entered.")
            .ensure(x => x.vin)
            .displayName('VIN')
            .required()
            .when(x => (!x.imo || x.imo.trim() == "") && (!x.eni || x.eni.trim() == "") && (!x.vin || x.vin.trim() == ""))
            .withMessage("Either IMO/ENI/VIN must be entered.")
            .ensure(x => x.vesselStatusId)
            .displayName('Status')
            .required()
            .ensure(x => x.vesselTypeId)
            .displayName('Vessel Type')
            .required()
            .ensure(x => x.dateOfBuild)
            .displayName('Date of Build')
            .required()
            .on(this.vessel);
    }

    public async save() {
        const validationResponse = await this.validationController.validate();
        if (validationResponse.valid) {
            try {
                const response = await this.vesselService.saveVessel({ vessel: this.vessel });
                if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                }
                else {
                    this.notificationClientService.show("Add Vessel Successful!", "A new vessel has been added.", NotificationType.Success, 5000);
                    this.dialogClientService.hide();
                }
            } catch (exception) {
                if (exception.validationResult && exception.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(exception.validationResult);
                }
                else if (!exception || !exception.content || exception.content == "") {
                    this.notificationClientService.show("Error!", "Vessel could not be added.", NotificationType.Error, 5000);
                }
            }
        } else {
            //Filter out validations having the 'avoid-notification' tag
            this.notificationClientService.show("Validation Error",
                validationResponse.results.filter(validation => validation.rule.tag !== this.avoidNotificationTag)
                    .map(validation => validation.message)
                    .filter(validationMessage => validationMessage != null)
                    .join("<br>"),
                NotificationType.Error);
        }
    }

    cancel() {
        this.dialogClientService.hide();
    }

    @computedFrom("vessel.eni")
    get disableVin() {
        return this.vessel && this.vessel.eni && this.vessel.eni.trim().length > 0;
    }

    @computedFrom("vessel.vin")
    get disableEni() {
        return this.vessel && this.vessel.vin && this.vessel.vin.trim().length > 0;
    }

    /**
    * Callback method handling when the IMO, VIN, or ENI properties change
    */
    imoVinEniChanged() {
        //Get current input element placeholder
        const elementPlaceholderText = document.activeElement.getAttribute('placeholder');

        //If the current input is empty, remove focus from it (for error validation to style it)
        if (!(document.activeElement as HTMLInputElement).value)
            (document.activeElement as HTMLElement).blur();


        //Re-validate the fields (to remove/add the validation error styling if needed)
        setTimeout(() => {
            this.validationController.validate({ object: this.vessel, propertyName: 'imo' });
            this.validationController.validate({ object: this.vessel, propertyName: 'vin' });
            this.validationController.validate({ object: this.vessel, propertyName: 'eni' });

            //If the current input is empty, re-apply focus to it
            if (!(document.activeElement as HTMLInputElement).value)
                (document.querySelectorAll(`input[placeholder="${elementPlaceholderText}"]`)[0] as HTMLInputElement).focus();
        }, 10);
    }

    getVesselTypeParameters() {
        return "null,true";
    }

    getCompanyFormat() {
        return "{0} ({1})";
    }

    getCompanyParmaters() {
        return "fullName,owCode";
    }
}